import { required } from '@vuelidate/validators';
import { useNuxtApp } from 'nuxt/app';
import { mapState } from 'pinia';

import productDataMixin from '~~/common/mixins/productDataMixin';
import { epsRound, getLocaleNum, uuid4 } from '~~/common/utils';
import { getQuantityStepSizeForUnit, unitConverter, unitNames } from '~~/common/utils/variant';
import { PUSH_ADD_TO_CART, PUSH_REMOVE_CART_ITEM } from '~~/common/plugins/aw-analytics.js';

import urlMixin from '~~/common/mixins/urlMixin';

import { useNotificationStore } from '~~/common/stores/notification';
import { useModalStore } from '~~/common/stores/modal';
import { useOrderStore } from '~~/common/stores/order';
import { useSearchStore } from '~~/common/stores/search';
import { useUserInterfaceStore } from '~~/common/stores/userInterface';
import { useCategoryStore } from '~~/common/stores/category';
import { useConfigStore } from '~~/common/stores/config';
import { useUserStore } from '~~/common/stores/user';
import { useDeliveryStore } from '~~/shop/stores/delivery';
import { useStockInfoStore } from '~~/dealer/stores/stockInfo';

const quantityRange = (value, vm) => {
  if (vm.quantityOptions != null) {
    const cnt = parseFloat((value?.count != null) ? value.count : value);
    return Boolean(vm.quantityOptions.filter(e => parseFloat(e.count) === cnt).length);
  } else {
    return true;
  }
};

export default {
  mixins: [
    productDataMixin,
    urlMixin,
  ],
  validations: {
    quantity: {
      model: {
        required,
        quantityRange,
      },
    },
  },
  props: {
    styling: {
      type: String,
      default: '',
    },
    buttonStyle: {
      type: [Object, Array, String],
      default: '',
    },
    isHorizontal: {
      type: Boolean,
      default: false,
    },
    productData: {
      type: Object,
      required: true,
    },
    dropdownSide: {
      type: String,
      default: 'bottom',
      validator: s => ['bottom', 'top'].includes(s),
    },
    cardSize: {
      type: String,
      default: 'xl',
    },
    viewMode: {
      type: String,
      default: 'card',
      validator: value => ['card', 'details'].includes(value),
    },
    defaultStyleShrink: {
      type: Boolean,
      default: false,
    },
    defaultStyleStretch: {
      type: Boolean,
      default: true,
    },
    basketStockType: {
      type: String,
      default: null,
    },
    showLoadingSpinner: {
      type: Boolean,
      default: true,
    },
    isMobileDesign: {
      type: Boolean,
      default: true,
    },
    isSmall: {
      type: Boolean,
      default: false,
    },
    isBasketPage: {
      type: Boolean,
      default: false,
    },
    isVersion2: {
      type: Boolean,
      default: false,
    },
    externalQuantity: {
      type: Object,
      default: null,
      required: false,
    },
  },
  emits: [
    'quantity-change-update-cart',
    'quantity-change',
    'basketItemChange',
    'added-to-cart',
  ],
  data () {
    return {
      isRemoveModalAppears: true,
      stockType: this.basketStockType || 'store',
      quantityType: '',
      quantityTypeOptions: [
        {
          id: 2,
          code: 'db',
          label: this.$awt('aw.global.quantity.db'),
          options: [],
        },
        {
          id: 1,
          code: 'kg',
          label: this.$awt('aw.global.quantity.kg'),
          options: [],
        },
      ],
      quantity: {
        type: 'number',
        model: '',
        name: 'quantity',
        label: '',
        isBoxError: false,
        tokens: {
          required: 'aw.common.add_to_basket.quantity_required',
        },
      },
      cartTimeout: null,
      quantityTempChange: 0,
      freezeNumberDisplay: null,
      ignoreLoyaltyPriceModalId: `ignoreLoyaltyPriceModal_${uuid4()}`,
      ignoreLoyaltyPriceModalWatcher: {
        onOk: null,
        onCancel: null,
      },
    };
  },
  computed: {
    hasStock () {
      return !__AW_IS_SHOP__;
    },
    ...mapState(useOrderStore, {
      basketItem (state) {
        for (const { packages } of state.order?.splits || []) {
          for (const {
            discriminator,
            items,
          } of packages) {
            if (this.hasStock && discriminator.servingMode !== this.stockType) {
              continue;
            }
            const foundItem = items.find((item) => {
              return item.variant?.productId === this.productData.selectedVariant.productId;
            });
            if (foundItem) {
              return foundItem;
            }
          }
        }
      },
    }),
    ...mapState(useUserStore, {
      allowCartItemOperations (state) {
        return __AW_IS_SHOP__ || state.isEcomUser;
      },
    }),
    ...mapState(useUserInterfaceStore, {
      screenRange: state => state.mediaQueries,
      addToCartLoading (state) {
        return state.loading.includes(this.addToCartItemId);
      },
      updateCartLoading (state) {
        return state.loading.includes(this.updateCartItemId) || state.loading.includes(this.removeCartItemId);
      },
    }),
    ...mapState(useDeliveryStore, {
      deliverySetup: state => state.setup,
    }),
    ...mapState(useStockInfoStore, {
      stocks (state) {
        return state.getFullStockInfo(this.productId);
      },
    }),
    ...mapState(useModalStore, {
      confirmModal: state => state.modals.confirmModal,
    }),
    ...mapState(useCategoryStore, {
      currentCategory: state => state.category,
    }),
    updateCartItemId () {
      return `update-cart-item-${this.basketItem?.uuid}`;
    },
    removeCartItemId () {
      return `remove-cart-item-${this.basketItem?.uuid}`;
    },
    addToCartItemId () {
      return `add-to-cart-${this.getVariant?.id}-${this.stockType}`;
    },

    basketQuantity () {
      return this.basketItem?.quantity?.quantity ? epsRound(this.basketItem.quantity.quantity) : 0;
    },
    quantityStepSize () {
      return this.getVariant.cartInfo.quantityStepSize;
    },
    quantitySteps () {
      return this.getVariant.cartInfo.quantitySteps;
    },
    quantityOptions () {
      return this.quantityTypeOptions.find(e => e.code === this.quantityType)?.options || [];
    },
    hasNextQuantitySep () {
      let q;
      const index = (this.basketItem
        ? this.quantityOptions?.findIndex((option) => {
          if (q === undefined) {
            q = epsRound(this.basketItem?.quantity?.quantity);
          }
          return option.count === q;
        })
        : (this.quantityOptions?.length === 1
          ? Infinity
          : (this.quantityTempChange - 1)
        )
      );
      return index < this.quantityOptions?.length - 1;
    },
    hasPrevQuantitySep () {
      let q;
      const index = (this.basketItem
        ? this.quantityOptions?.findIndex((option) => {
          if (q === undefined) {
            q = epsRound(this.basketItem?.quantity?.quantity);
          }
          return option.count === q;
        })
        : (this.quantityTempChange - 1)
      );
      return index > 0;
    },
    price () {
      return this.$price({ gross: this.productData.selectedVariant.price.grossDiscounted }, this.productData.selectedVariant.price.currency);
    },
    getUnitName () {
      return unitNames[this.quantityType] === 0 ? 'piece' : 'weight';
    },
    preferredVariant () {
      return this.basketItem?.variant ?? this.getVariant;
    },
    quantityRounded () {
      const uc = (to, from) => {
        try {
          return unitConverter({
            from,
            to,
            weightPerPiece: this.preferredVariant?.loose?.weightPerPiece,
          });
        } catch (err) {
          this.$logger.error(err);
          return from;
        }
      };
      const unit = this.getUnitName;
      if (this.quantityTempChange) {
        const newVal = (
          this.quantityTempChange *
          getQuantityStepSizeForUnit(this.getVariant, unit) +
          (this.basketItem
            ? uc(unit, {
              value: this.basketQuantity,
              unit: this.basketItem.quantity.type,
            }).value
            : 0
          )
        );
        return {
          value: epsRound(newVal),
          unit,
        };
      } else {
        return this.basketItem
          ? {
            value: this.basketQuantity,
            unit: this.basketItem?.quantity?.type,
          }
          : {
            value: this.quantityOptions[0]?.count,
            unit,
          }
        ;
      }
    },
  },
  watch: {
    basketStockType (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.stockType = newVal;
      }
    },
    stockType (newVal, oldVal) {
      if (newVal !== oldVal) {
        this.initQuantityOptions();
      }
    },
    quantityType (newValue, oldValue) {
      const orderStore = useOrderStore();
      this.setQuantityOptionsAndLabel();
      this.quantityTempChange = 0;
      if (oldValue && newValue && this.basketItem?.uuid) {
        const {
          quantity,
          quantityType,
        } = {
          quantity: this.quantityOptions[0]?.count,
          quantityType: this.getUnitName,
        };
        const updateAction = orderStore.updateCartItemQuantity({
          uuid: this.basketItem.uuid,
          quantity,
          quantityType,
          fromBasket: this.isBasketPage,
        });
        this.$emit('quantity-change-update-cart', {
          quantity,
          quantityType,
          updateAction,
        });
      }
    },
    'basketItem.quantity.type' () {
      this.initQuantityType();
    },
    screenRange () {
      this.setQuantityOptionsAndLabel();
    },
    basketQuantity (newValue) {
      if (!newValue) {
        this.resetQuantity();
      }
      if (newValue) {
        this.setQuantityModelValue();
      }
    },
    'quantity.model': {
      deep: true,
      async handler (newValue, oldValue) {
        const orderStore = useOrderStore();
        // when change quantity with aw-select
        if (oldValue && newValue && !this.quantityTempChange && newValue?.count !== oldValue?.count && this.basketItem?.uuid) {
          // NOTE: when a basketItem changes it propages the change to
          // this component, but we do NOT want to run updateCartItemQuantity
          // when the triggerer the cartItem itself.
          // This could happen:
          // 1. when a single cartItem is referenced in multiple
          //    components so the change is triggered from the outside.
          // 2. when the cartItem uuid changes every watcher on cartItem
          //    could be triggered (for example servingMode change
          //    changes cartItem).
          if (this.getUnitName !== this.basketItem.quantity.type || newValue.count !== this.basketItem.quantity.quantity) {
            const {
              quantity,
              quantityType,
            } = {
              quantity: newValue.count,
              quantityType: this.getUnitName,
            };
            const updateAction = orderStore.updateCartItemQuantity({
              uuid: this.basketItem.uuid,
              quantity,
              quantityType,
              fromBasket: this.isBasketPage,
            });
            this.$emit('quantity-change-update-cart', {
              quantity,
              quantityType,
              updateAction,
            });
            await updateAction;
            this.fetchStockInfo();
            const notificationStore = useNotificationStore();

            notificationStore.pushSuccess({
              manualClose: false,
              screenReaderOnly: true,
              text: {
                title: this.$awt('aw.common.product_card.product_quantity_in_cart', {
                  product_name: this.productName,
                  quantity: `${quantity} ${this.$awt(`aw.global.quantity.${quantityType}`)}`,
                }),
              },
            });
          }
        }
        this.$emit('quantity-change', { ...newValue, ...{ unit: this.quantityType } });
      },
    },
    quantityTempChange: {
      deep: true,
      handler () {
        // if change quantity with +/- button
        this.quantity.model = this.quantityOptions.find(item => item?.count === this.quantityRounded?.value);
      },
    },
    deliverySetup (newValue) {
      const modalStore = useModalStore();
      // check if user enter delivery data, and user has previously saved cart data (clicked the add to basket button)
      // the watcher run each visible aw-add-to-basket component and we check the saved cart data and this component used variant data
      if (newValue && modalStore.savedAddToBasketData?.data?.objectId === this.getVariant.id.toString()) {
        this.addToCart(modalStore.savedAddToBasketData);
      }
    },
    basketItem: {
      immediate: true,
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.quantityTempChange = 0;
          if (this.isMounted) {
            this.setQuantityModelValue();
          }
          this.$emit('basketItemChange', newVal);
        }
      },
    },
  },
  mounted () {
    const modalStore = useModalStore();
    this.isMounted = true;
    this.initQuantityType();

    this.quantityTempChange = 0;
    this.initQuantityOptions();
    this.setQuantityOptionsAndLabel();
    // if we are on product page. the watcher here doesn't works, because the delivery setup has changed, then this component is rerendered. in this case use mounted instead watcher.
    if (modalStore.savedAddToBasketData?.data?.objectId === this.getVariant.id.toString()) {
      this.addToCart(modalStore.savedAddToBasketData);
    }
    this.setQuantityModelValue();
    this.fetchStockInfo();
  },
  methods: {
    fetchStockInfo () {
      if (!__AW_IS_SHOP__) {
        const stockInfoStore = useStockInfoStore();
        stockInfoStore.fetchStock({
          productIds: [this.productId],
          details: false,
        });
      }
    },
    resetQuantity () {
      this.quantity.model = this.externalQuantity ?? this.quantityOptions[0];
    },
    initQuantityType () {
      // set unit switcher to basket stored quantity type
      if (unitNames[this.basketItem?.quantity?.type] === 0) {
        this.quantityType = 'db';
      } else if (unitNames[this.basketItem?.quantity?.type] === 1) { // set unit switcher to basket stored quantity type
        this.quantityType = 'kg';
      } else { // if not stored item in basket, use default
        this.quantityType = this.variantUnit;
      }
    },
    setQuantityModelValue () {
      if (this.externalQuantity && !this.basketItem) {
        this.quantity.model = this.externalQuantity;
        return;
      }

      if (this.basketQuantity && this.quantity.model?.count !== this.basketQuantity) {
        this.quantity.model = this.quantityOptions.find(item => item?.count === this.basketQuantity);
      }
    },
    changeTempByNum (num) {
      const maximumQuantity = this.quantityOptions[this.quantityOptions.length - 1].count;
      this.quantityTempChange += num;
      if (this.quantityRounded.value > maximumQuantity || this.quantityRounded.value <= 0) {
        this.quantityTempChange -= num;
        return false;
      }
      return true;
    },
    increase () {
      const orderStore = useOrderStore();
      clearTimeout(this.cartTimeout);
      this.changeTempByNum(1);
      this.cartTimeout = setTimeout(() => {
        this.freezeNumberDisplay = this.quantityRounded;
        const {
          quantity,
          quantityType,
        } = {
          quantity: this.quantityRounded.value,
          quantityType: this.quantityRounded.unit,
        };
        const savedQuantity = this.basketItem.quantity.quantity;
        const updateAction = orderStore.updateCartItemQuantity({
          uuid: this.basketItem.uuid,
          quantity,
          quantityType,
          fromBasket: this.isBasketPage,
        }).finally(() => {
          this.quantityTempChange = 0;
          this.freezeNumberDisplay = null;
          this.fetchStockInfo();

          const quantityData = {
            quantity: quantity - savedQuantity,
            type: quantityType,
          };

          this.$awAnalytics[PUSH_ADD_TO_CART]({
            productData: this.productData,
            productList: this.currentCategory?.name,
            quantityData,
          });

          const notificationStore = useNotificationStore();

          notificationStore.pushSuccess({
            manualClose: false,
            screenReaderOnly: true,
            text: {
              title: this.$awt('aw.common.product_card.product_quantity_in_cart', {
                product_name: this.productName,
                quantity: `${quantity} ${this.$awt(`aw.global.quantity.${quantityType}`)}`,
              }),
            },
          });
        });
        this.$emit('quantity-change-update-cart', {
          quantity,
          quantityType,
          updateAction,
        });
      }, 512);
    },
    decrease () {
      const orderStore = useOrderStore();
      clearTimeout(this.cartTimeout);
      if (!this.changeTempByNum(-1)) {
        this.removeFromCart();
      } else {
        this.cartTimeout = setTimeout(() => {
          this.freezeNumberDisplay = this.quantityRounded;
          const {
            quantity,
            quantityType,
          } = {
            quantity: this.quantityRounded.value,
            quantityType: this.quantityRounded.unit,
          };
          const callWhenSucceeds = this.$awAnalytics[PUSH_REMOVE_CART_ITEM]({
            uuid: this.basketItem.uuid,
            newQuantity: this.quantityRounded,
          });
          const updateAction = orderStore.updateCartItemQuantity({
            uuid: this.basketItem.uuid,
            quantity,
            quantityType,
            fromBasket: this.isBasketPage,
          }).finally(() => {
            callWhenSucceeds();
            this.quantityTempChange = 0;
            this.freezeNumberDisplay = null;
            this.fetchStockInfo();

            const notificationStore = useNotificationStore();

            notificationStore.pushSuccess({
              manualClose: false,
              screenReaderOnly: true,
              text: {
                title: this.$awt('aw.common.product_card.product_quantity_in_cart', {
                  product_name: this.productName,
                  quantity: `${quantity} ${this.$awt(`aw.global.quantity.${quantityType}`)}`,
                }),
              },
            });
          });
          this.$emit('quantity-change-update-cart', {
            quantity,
            quantityType,
            updateAction,
          });
        }, 512);
      }
    },
    removeFromCart () {
      if (this.isRemoveModalAppears) {
        this.$refs.removeModal.openModal({ id: this.basketItem.uuid });
      } else {
        this.removeItem();
      }
    },
    async removeItem () {
      const orderStore = useOrderStore();
      await orderStore.removeCartItem({
        uuid: this.basketItem.uuid,
        fromBasket: this.isBasketPage,
      });

      this.fetchStockInfo();

      orderStore.removeReturnUsedItem(this.productData.selectedVariant.id);
    },
    initQuantityOptions () {
      const { $i18n } = useNuxtApp();
      const orderStore = useOrderStore();
      const createOptions = (unitToken, qss, stepsToTake) => {
        return Array.from((new Array(stepsToTake)).keys(), (i) => {
          const n = epsRound((i + 1) * qss);
          const localeNum = getLocaleNum($i18n.locale.value, n);
          return {
            count: n,
            localeCount: localeNum,
            name: `${localeNum}\xA0${this.$awt(unitToken)}`,
          };
        });
      };

      const pieceQss = this.isConvertible ? this.quantityStepSize / this.preferredVariant?.loose?.weightPerPiece : this.quantityStepSize;
      let pieceSteps = this.quantitySteps;
      if (this.basketItem && !__AW_IS_SHOP__) {
        const {
          servingMode,
          deliveryMode,
        } = orderStore.getOrderItemDiscriminator(orderStore.order, this.basketItem);
        if (servingMode === 'store' && deliveryMode === 'store') {
          const configStore = useConfigStore();
          pieceSteps = configStore.configDealer.productMaxQuantityChoicesStoreServing;
        }
      }
      this.quantityTypeOptions.find(({ code }) => code === 'db').options = createOptions('aw.global.quantity.db', pieceQss, pieceSteps);
      this.quantityTypeOptions.find(({ code }) => code === 'kg').options = createOptions('aw.global.quantity.kg', this.quantityStepSize, this.quantitySteps);
    },
    setQuantityOptionsAndLabel () {
      if (!this.basketItem && !this.externalQuantity) {
        this.quantity.model = this.quantityOptions[0];
      }
    },
    async addToCart (savedCartData = null, options) {
      const modalStore = useModalStore();
      const orderStore = useOrderStore();
      const deliveryStore = useDeliveryStore();
      const notificationStore = useNotificationStore();
      const quantityCount = (typeof this.quantity.model === 'object')
        ? this.quantity.model.count
        : this.quantity.model
      ;

      const createDiscrimiator = async () => {
        if (__AW_IS_SHOP__) {
          return {};
        } else {
          const isInCart = orderStore.getFlattenedOrderItems(orderStore.order)?.some(i => i.variant.productId === this.productId);
          if (isInCart) {
            modalStore.setSureAddToCartModalProduct(this.productId);
            modalStore.setSureAddToCartModalForbiddenSelection(Object.keys(this.stocks).filter(s => s !== this.stockType));
            modalStore.openSureAddToCartModal();
            {
              let r;
              const p = new Promise((resolve) => {
                r = resolve;
              });

              const unsubscribe = modalStore.$onAction(
                ({
                  name,
                  after,
                }) => {
                  after((/* result */) => {
                    if (name === 'setSureAddToCartModalSelection') {
                      r();
                    }
                  });
                },
              );

              await p;
              unsubscribe();
              modalStore.setSureAddToCartModalForbiddenSelection([]);
            }
            if (modalStore.sureAddToCartModal.selection) {
              return {
                checkoutType: 'DEALER',
                companyNo: '',
                servingMode: modalStore.sureAddToCartModal.selection,
                deliveryMode: this.stocks[modalStore.sureAddToCartModal.selection].defaultDeliveryMode(this.product.shipmentMethodCode),
              };
            }
            throw new Error('stock selection aborted');
          } else {
            return {
              checkoutType: 'DEALER',
              companyNo: '',
              servingMode: this.stockType,
              deliveryMode: this.stocks[this.stockType].defaultDeliveryMode(this.product.shipmentMethodCode),
            };
          }
        }
      };
      let discriminator;
      try {
        discriminator = await createDiscrimiator();
      } catch (err) {
        if (err.message === 'stock selection aborted') {
          return;
        } else {
          throw err;
        }
      }

      let cartData = {
        id: 'current',
        objectType: 'variant',
        data: {
          objectId: this.getVariant.id + '',
          quantity: {
            quantity: parseFloat(quantityCount),
            type: ['db', 'piece', 'szt.'].includes(this.quantityType) ? 'piece' : 'weight',
          },
          discriminator,
          stockType: this.stockType,
        },
        fromBasket: this.isBasketPage,
      };

      if (__AW_IS_SHOP__ && !deliveryStore.setup) {
        modalStore.openDeliveryMethodModal({ cartData });
        return;
      }

      // If alwaysShowLoyaltyPrice but loyalty price is invalid, the modal ask the user if, he/she want add to cart on regular price
      if (__AW_IS_SHOP__ && !this.productData?.selectedVariant?.isLoyaltyPriceValid && this.alwaysShowLoyaltyPrice && !options?.ignoreLoyaltyPrice) {
        modalStore.showConfirmModal({
          closable: true,
          data: {
            confirmTitle: this.$awt('aw.profile.loyalty_card.modal.title'),
            confirmText: this.$awt('aw.profile.loyalty_card.modal.text', { price: this.price }),
            confirmOkButton: this.$awt('aw.profile.loyalty_card.modal.confirm'),
            confirmCancelButton: this.$awt('aw.global.cancel'),
            confirmOkButtonStyle: 'primary-dark',
            confirmCancelButtonStyle: 'secondary-dark',
            confirmOkActionType: this.ignoreLoyaltyPriceModalId,
          },
        });
        this.ignoreLoyaltyPriceModalWatcher.onOk = this.$watch('confirmModal.confirmOk.type', (newValue) => {
          if (newValue === this.ignoreLoyaltyPriceModalId) {
            const modalStore = useModalStore();
            this.addToCart(modalStore.savedAddToBasketData, { ignoreLoyaltyPrice: true });
            this.ignoreLoyaltyPriceModalWatcher.onOk();
            this.ignoreLoyaltyPriceModalWatcher.onCancel();
          }
        });
        this.ignoreLoyaltyPriceModalWatcher.onCancel = this.$watch('confirmModal.confirmCancel.type', () => {
          this.ignoreLoyaltyPriceModalWatcher.onOk();
          this.ignoreLoyaltyPriceModalWatcher.onCancel();
        });
        return;
      }

      // use saved cart data, if user click the add to basket button before open delivery modal
      if (savedCartData) {
        cartData = savedCartData;
        modalStore.resetAddToBasketData();
      }
      const searchStore = useSearchStore();
      searchStore.searchesLog({
        type: 'addToCart',
        resultId: parseInt(this.productId),
      });

      const result = await orderStore.addToCart(cartData);

      this.fetchStockInfo();
      // All ok
      if (result) {
        this.$emit('added-to-cart', true);
        notificationStore.pushSuccess({
          manualClose: false,
          screenReaderOnly: true,
          text: {
            title: this.$awt('aw.common.product_card.screen_reader_label.successfully_added_to_card', {
              product_name: this.productName,
              quantity: `${cartData.data.quantity.quantity} ${this.quantityType}`,
            }),
          },
        });

        this.setQuantityOptionsAndLabel();
        this.quantityTempChange = 0;
        this.$awAnalytics[PUSH_ADD_TO_CART]({
          productData: this.productData,
          productList: this.currentCategory?.name,
          quantityData: {
            quantity: cartData.data.quantity.quantity,
            type: this.quantityType,
          },
        });
      } else if (result == null) {
        // do nothing
      } else { // Error
        this.$emit('added-to-cart', false);
        notificationStore.pushSuccess({
          manualClose: false,
          screenReaderOnly: true,
          text: {
            title: this.$awt('aw.common.product_card.screen_reader_label.error', { product_name: this.productName }),
          },
        });
      }
    },
  },
};
